/**
 * Quality: lab reports, release, lab-fail holds (uc-core src/modules/lab/controllers):
 *   POST /lab-reports  multipart { reportNo, sealPayload, labOrgId?, testedAt, pdf, resultsFile }   lab-reports:create
 *   GET  /lab-reports ?unitId&sampleId&status&overall&search · /lab-reports/:id (rows, pdf_url)      lab-reports:read
 *   GET  /units/:id/lab-status · /units/:id/holds · /units/:id/trace-back/samples
 *   GET  /release-queue ?state=WAITING|HELD|RELEASED|ALL · /release-decisions ?packLotId&unitId     pack-lots:read
 *   GET  /pack-lots/:id/release-checklist
 *   POST /pack-lots/:id/decisions { decision: RELEASE|HOLD, reason?, attested, reauthToken }        pack-lots:release
 *   POST /units/:id/dispositions { decision, qtyKg, pouches?, basis, confirmingReportId?, waiverNote?, reason, reauthToken }
 *   POST /units/:id/decisions { decision: RELEASE, reason, reauthToken }   units:lift-lab-hold · 409 HOLD_NOT_LIFTABLE
 *   POST /auth/reauth { password (encrypted like the login) } → { reauth_token, expires_at }
 */
import { useQuery } from '@tanstack/react-query'
import { api, isApiError } from '@/api'
import { encryptPassword } from '@/auth/passwordCipher'
import { errorDetail, errorMessage, getOne, getPage, useConsoleMutation, usePageQuery, type PageParams } from './core'
import { ck } from './keys'
import { arr, iso, num, numOrNull, obj, str, strOrNull } from './mappers'
import type { Checklist } from '../model/release'

// ============================== signature ==============================

/** Re-enter the password before a signature: a token valid for 5 minutes (401 when it does not match). */
export async function reauth(password: string): Promise<string> {
  const encrypted = await encryptPassword(password)
  const r = obj(await api.post('/auth/reauth', { password: encrypted }))
  const token = str(r.reauth_token ?? r.reauthToken)
  if (!token) throw new Error('The server did not return a signing token.')
  return token
}

// ============================== lab reports (F7.5) ==============================

export interface LabReportRow {
  id: string
  reportNo: string
  status: string
  overall: string | null
  testedAt: string | null
  receivedAt: string | null
  unitId: string
  unitCode: string
  unitType: string | null
  sampleKind: string | null
  labOrgId: string | null
  labName: string | null
  enteredById: string | null
  enteredByName: string | null
  rows: number
  rowsNotPass: number
}
export const mapLabReportRow = (r: Record<string, unknown>): LabReportRow => ({
  id: str(r.id),
  reportNo: str(r.report_no),
  status: str(r.status),
  overall: strOrNull(r.overall),
  testedAt: strOrNull(r.tested_at),
  receivedAt: iso(r.received_at),
  unitId: str(r.unit_id),
  unitCode: str(r.unit_code),
  unitType: strOrNull(r.unit_type),
  sampleKind: strOrNull(r.sample_kind),
  labOrgId: strOrNull(r.lab_org_id),
  labName: strOrNull(r.lab_name),
  enteredById: strOrNull(r.entered_by_id),
  enteredByName: strOrNull(r.entered_by_name),
  rows: num(r.rows),
  rowsNotPass: num(r.rows_not_pass),
})

export interface LabRow {
  panel: string
  analyte: string
  value: string | null
  uom: string
  loq: string
  limitValue: string | null
  flag: string
  text: string
}
const mapLabRow = (x: Record<string, unknown>): LabRow => ({
  panel: str(x.panel),
  analyte: str(x.analyte),
  value: strOrNull(x.value),
  uom: str(x.uom),
  loq: str(x.loq),
  limitValue: strOrNull(x.limit_value),
  flag: str(x.result_flag, 'PASS'),
  text: str(x.result_text),
})

export interface LabReportDetail extends LabReportRow {
  sampleId: string | null
  sealPayload: string | null
  accreditationNo: string | null
  accreditationValidTo: string | null
  pdfUrl: string | null
  supersedesId: string | null
  supersededById: string | null
  results: LabRow[]
}
export const mapLabReportDetail = (r: Record<string, unknown>): LabReportDetail => ({
  ...mapLabReportRow(r),
  sampleId: strOrNull(r.sample_id),
  sealPayload: strOrNull(r.seal_payload),
  accreditationNo: strOrNull(r.accreditation_no),
  accreditationValidTo: strOrNull(r.accreditation_valid_to),
  pdfUrl: strOrNull(r.pdf_url),
  supersedesId: strOrNull(r.supersedes_id),
  supersededById: strOrNull(r.superseded_by_id),
  results: arr(r.rows).map(mapLabRow),
})

export function useLabReports(
  f: PageParams & { unitId?: string; overall?: string; search?: string } = {},
  enabled = true,
) {
  return usePageQuery(
    [...ck.quality, 'reports', f],
    () => getPage('/lab-reports', { page: 1, limit: 50, ...f }, mapLabReportRow),
    { enabled },
  )
}
export function useLabReport(id: string | null | undefined) {
  return useQuery({
    queryKey: [...ck.quality, 'reports', 'detail', id],
    enabled: Boolean(id),
    queryFn: () => getOne(`/lab-reports/${id}`, mapLabReportDetail),
    staleTime: 30 * 60_000,
  })
}

export interface AttachResult {
  id: string
  status: string
  overall: string | null
  unitId: string
  warnings: string[]
  rows: LabRow[]
  heldUnitIds: string[]
  exceptionId: string | null
}
export function useAttachLabReport() {
  return useConsoleMutation<
    {
      sealPayload: string
      labOrgId: string
      reportNo: string
      testedAt: string
      pdf: File
      resultsFile: File
    },
    AttachResult
  >({
    mutationFn: async (v) => {
      const fd = new FormData()
      fd.append('reportNo', v.reportNo.trim())
      fd.append('sealPayload', v.sealPayload.trim())
      if (v.labOrgId) fd.append('labOrgId', v.labOrgId)
      fd.append('testedAt', v.testedAt)
      fd.append('pdf', v.pdf, v.pdf.name)
      fd.append('resultsFile', v.resultsFile, v.resultsFile.name)
      const r = obj(await api.post('/lab-reports', fd, { timeoutMs: 120_000 }))
      return {
        id: str(r.id),
        status: str(r.status),
        overall: strOrNull(r.overall),
        unitId: str(r.unit_id),
        warnings: arr<string>(r.warnings).map(String),
        rows: arr(r.rows).map(mapLabRow),
        heldUnitIds: arr<string>(r.held_unit_ids).map(String),
        exceptionId: strOrNull(r.exception_id),
      }
    },
    invalidate: [ck.quality, ck.pack, ck.exceptions, ck.samples],
    success: (r) =>
      r.heldUnitIds.length
        ? `Report saved · ${r.heldUnitIds.length} items put on hold`
        : 'Report saved',
    errorTitle: 'Report not saved',
  })
}

export interface LabStatus {
  overall: string
  requiredPanels: string[]
  coveredPanels: string[]
  reportIds: string[]
  latestTestedAt: string | null
  labUnitId: string | null
}
export function useLabStatus(unitId: string | null | undefined) {
  return useQuery({
    queryKey: [...ck.quality, 'lab-status', unitId],
    enabled: Boolean(unitId),
    queryFn: () =>
      getOne(`/units/${unitId}/lab-status`, (r): LabStatus => ({
        overall: str(r.overall, 'PENDING'),
        requiredPanels: arr<string>(r.required_panels).map(String),
        coveredPanels: arr<string>(r.covered_panels).map(String),
        reportIds: arr<string>(r.report_ids).map(String),
        latestTestedAt: strOrNull(r.latest_tested_at),
        labUnitId: strOrNull(r.lab_unit_id),
      })),
  })
}

// ============================== release (F7.6) ==============================

export interface QueueRow {
  id: string
  code: string
  status: string
  hold: string
  packDate: string | null
  bestBefore: string | null
  pouchCount: number | null
  skuCode: string
  skuName: string
  latestDecision: {
    decision: string
    reason: string | null
    auto: boolean
    decidedAt: string | null
    decidedById: string | null
  } | null
}
export const mapQueueRow = (r: Record<string, unknown>): QueueRow => {
  const d = r.latest_decision ? obj(r.latest_decision) : null
  return {
    id: str(r.id),
    code: str(r.code),
    status: str(r.status),
    hold: str(r.hold, 'NONE'),
    packDate: strOrNull(r.pack_date),
    bestBefore: strOrNull(r.best_before),
    pouchCount: numOrNull(r.pouch_count),
    skuCode: str(r.sku_code),
    skuName: str(r.sku_name),
    latestDecision: d
      ? {
          decision: str(d.decision),
          reason: strOrNull(d.reason),
          auto: d.auto === true,
          decidedAt: iso(d.decided_at),
          decidedById: strOrNull(d.decided_by_id),
        }
      : null,
  }
}
export type QueueState = 'WAITING' | 'HELD' | 'RELEASED' | 'ALL'
export function useReleaseQueue(f: PageParams & { state?: QueueState; search?: string }) {
  return usePageQuery([...ck.quality, 'queue', f], () =>
    getPage('/release-queue', { page: 1, limit: 50, ...f }, mapQueueRow),
  )
}

export const mapChecklist = (r: Record<string, unknown>): Checklist => ({
  packLotId: str(r.pack_lot_id),
  items: arr(r.items).map((i) => ({
    key: str(i.key),
    ok: i.ok === true,
    detail: arr<string>(i.detail).map(String),
    evidence: arr(i.evidence).map((e) => ({
      type: str(e.type),
      id: str(e.id),
      code: strOrNull(e.code),
    })),
  })),
  canRelease: r.can_release === true,
  labReportIds: arr<string>(r.lab_report_ids).map(String),
  computedAt: iso(r.computed_at),
})
export function useChecklist(packLotId: string | null | undefined) {
  return useQuery({
    queryKey: [...ck.quality, 'checklist', packLotId],
    enabled: Boolean(packLotId),
    queryFn: () => getOne(`/pack-lots/${packLotId}/release-checklist`, mapChecklist),
  })
}

export function useReleaseDecision() {
  return useConsoleMutation<
    {
      packLotId: string
      decision: 'RELEASE' | 'HOLD'
      reason?: string
      attested: boolean
      password: string
    },
    { status: string; hold: string }
  >({
    mutationFn: async ({ packLotId, password, ...body }) => {
      const reauthToken = await reauth(password)
      const r = obj(
        await api.post(`/pack-lots/${packLotId}/decisions`, {
          ...body,
          reason: body.reason?.trim() || undefined,
          reauthToken,
        }),
      )
      const pl = obj(r.pack_lot)
      return { status: str(pl.status), hold: str(pl.hold, 'NONE') }
    },
    invalidate: [ck.quality, ck.pack],
    success: (_r, v) =>
      v.decision === 'RELEASE' ? 'Signed and released' : 'Signed: the lot is on hold',
    errorTitle: 'Not signed',
  })
}

export interface DecisionRow {
  id: string
  decision: string
  auto: boolean
  reason: string | null
  decidedAt: string | null
  unitCode: string
  packLotCode: string | null
  decidedByName: string | null
}
export function useReleaseDecisions(
  f: PageParams & { packLotId?: string; unitId?: string },
  enabled = true,
) {
  return usePageQuery(
    [...ck.quality, 'decisions', f],
    () =>
      getPage('/release-decisions', { page: 1, limit: 20, ...f }, (r): DecisionRow => ({
        id: str(r.id),
        decision: str(r.decision),
        auto: r.auto === true,
        reason: strOrNull(r.reason),
        decidedAt: iso(r.decided_at),
        unitCode: str(r.unit_code),
        packLotCode: strOrNull(r.pack_lot_code),
        decidedByName: strOrNull(obj(r.decided_by).name),
      })),
    { enabled },
  )
}

// ============================== lab-fail holds (F7-B1) ==============================

export interface HeldItem {
  unitId: string
  code: string
  unitType: string
  hold: string
  holdReason: string | null
  /** FAMILY (the failing family), SIBLING (the farmer's other lots), LOQ. */
  scope: string
  labReportId: string | null
  packLotId: string | null
  packLotCode: string | null
  packLotStatus: string | null
  packLotHold: string | null
  pouchCount: number | null
  locationCode: string | null
}
export function useHolds(unitId: string | null | undefined) {
  return useQuery({
    queryKey: [...ck.quality, 'holds', unitId],
    enabled: Boolean(unitId),
    queryFn: () =>
      getOne(`/units/${unitId}/holds`, (r) => ({
        exceptionId: strOrNull(r.exception_id),
        held: arr(r.held).map((h): HeldItem => ({
          unitId: str(h.unit_id),
          code: str(h.code),
          unitType: str(h.unit_type),
          hold: str(h.hold, 'NONE'),
          holdReason: strOrNull(h.hold_reason),
          scope: str(h.scope, 'FAMILY'),
          labReportId: strOrNull(h.lab_report_id),
          packLotId: strOrNull(h.pack_lot_id),
          packLotCode: strOrNull(h.pack_lot_code),
          packLotStatus: strOrNull(h.pack_lot_status),
          packLotHold: strOrNull(h.pack_lot_hold),
          pouchCount: numOrNull(h.pouch_count),
          locationCode: strOrNull(h.location_code),
        })),
      })),
  })
}

export interface TraceBackLot {
  unitId: string
  code: string
  farmerId: string | null
  seasonCode: string | null
  hold: string
  sample: {
    id: string
    seal: string | null
    status: string
    retainUntil: string | null
    locationCode: string | null
  } | null
  sampleUsable: boolean
  exceptionId: string | null
}
export function useTraceBackSamples(unitId: string | null | undefined) {
  return useQuery({
    queryKey: [...ck.quality, 'trace-back', unitId],
    enabled: Boolean(unitId),
    queryFn: () =>
      getOne(`/units/${unitId}/trace-back/samples`, (r) => ({
        unitId: str(r.unit_id),
        unitCode: str(r.unit_code),
        farmersCount: num(r.farmers_count),
        oneFarmerExceptionId: strOrNull(r.one_farmer_exception_id),
        lots: arr(r.lots).map((l): TraceBackLot => {
          const s = l.retained_sample ? obj(l.retained_sample) : null
          return {
            unitId: str(l.unit_id),
            code: str(l.code),
            farmerId: strOrNull(l.farmer_id),
            seasonCode: strOrNull(l.season_code),
            hold: str(l.hold, 'NONE'),
            sample: s
              ? {
                  id: str(s.id),
                  seal: strOrNull(s.seal),
                  status: str(s.status),
                  retainUntil: strOrNull(s.retain_until),
                  locationCode: strOrNull(s.location_code),
                }
              : null,
            sampleUsable: l.sample_usable === true,
            exceptionId: strOrNull(l.exception_id),
          }
        }),
      })),
  })
}

export interface DisposeVars {
  unitId: string
  body: {
    decision: 'DIVERT' | 'DESTROY'
    qtyKg: string
    pouches?: number
    basis: 'RETEST' | 'WAIVED'
    confirmingReportId?: string
    waiverNote?: string
    reason: string
  }
}
export interface LiftResult {
  lifted: string[]
  /** unit id → why the server kept its hold (e.g. FAILING_REPORT_STILL_STANDS). */
  refused: Record<string, string>
}
/**
 * POST /units/:id/decisions { decision: RELEASE, reason, reauthToken } (units:lift-lab-hold): tries
 * each held unit with one password; a unit the server keeps held (409) is reported, not fatal.
 */
export function useLiftLabHolds() {
  return useConsoleMutation<{ unitIds: string[]; reason: string; password: string }, LiftResult>({
    mutationFn: async ({ unitIds, reason, password }) => {
      const reauthToken = await reauth(password)
      const out: LiftResult = { lifted: [], refused: {} }
      for (const id of unitIds) {
        if (out.lifted.includes(id)) continue
        try {
          const r = obj(await api.post(`/units/${id}/decisions`, { decision: 'RELEASE', reason, reauthToken }))
          for (const u of arr(r.units)) out.lifted.push(str(obj(u).id))
        } catch (e) {
          if (!isApiError(e) || e.kind !== 'conflict') throw e
          const body = errorDetail(e)
          const why = arr(obj(body.errors).refused ?? body.refused).map((x) => str(obj(x).why))
          out.refused[id] = why.join(', ') || errorMessage(e)
        }
      }
      return out
    },
    invalidate: [ck.quality, ck.pack, ck.exceptions, ck.lots, ck.stock],
    success: (r) =>
      r.lifted.length > 0
        ? `Hold lifted on ${r.lifted.length} item${r.lifted.length === 1 ? '' : 's'}`
        : 'Nothing could be lifted yet',
    errorTitle: 'Hold not lifted',
  })
}

/** Signs every row with one password (one reauth token, valid 5 minutes), in order. */
export function useDisposeAll() {
  return useConsoleMutation<{ rows: DisposeVars[]; password: string }, { done: string[] }>({
    mutationFn: async ({ rows, password }) => {
      const reauthToken = await reauth(password)
      const done: string[] = []
      for (const r of rows) {
        await api.post(`/units/${r.unitId}/dispositions`, { ...r.body, reauthToken })
        done.push(r.unitId)
      }
      return { done }
    },
    invalidate: [ck.quality, ck.pack, ck.exceptions, ck.lots, ck.stock],
    success: (r) => `Disposition signed for ${r.done.length} item${r.done.length === 1 ? '' : 's'}`,
    errorTitle: 'Disposition not signed',
  })
}
